import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, ArrowUp, ArrowRight } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { openWhatsAppHub } from "@/components/WhatsAppHub";

const HANDOFF_TOKEN = "[HANDOFF]";
const CONFIRMATION_MSG = "You're all set! 🧡 I've passed your request to the ROAMIES team. They'll follow up using your preferred contact method. In the meantime, I'm still here if you'd like to explore our adventures.";

// Distinctive Roamie mark: 8-point compass rose with a flame ember core inside an ink roundel.
// Complements the ROAMIES logo — never replaces it.
export const RoamieMark = ({ className = "w-8 h-8" }) => (
  <svg viewBox="0 0 40 40" className={className} aria-hidden="true">
    <circle cx="20" cy="20" r="18.5" fill="#0a0a0a" stroke="#ff4500" strokeWidth="1.6" />
    <path d="M20 5 L22.4 17.6 L35 20 L22.4 22.4 L20 35 L17.6 22.4 L5 20 L17.6 17.6 Z" fill="#ff4500" />
    <path d="M27.5 12.5 L21.2 18.8 L27.5 27.5 L18.8 21.2 L12.5 27.5 L18.8 21.2 L12.5 12.5 L21.2 18.8 Z" fill="#f7f7f5" opacity="0.85" />
    <circle cx="20" cy="20" r="3.2" fill="#ff4500" />
    <circle cx="20" cy="20" r="1.4" fill="#f7f7f5" />
  </svg>
);

// Subtle journey route line — decorative only, no real locations.
const RouteLine = ({ className = "" }) => (
  <svg viewBox="0 0 300 40" fill="none" className={className} aria-hidden="true" preserveAspectRatio="none">
    <path d="M0 32 C 60 10, 120 38, 180 18 S 280 8, 300 22" stroke="rgba(255,69,0,0.35)" strokeWidth="1.2" strokeDasharray="5 6" />
    <circle cx="4" cy="31" r="2.5" fill="#ff4500" opacity="0.7" />
    <circle cx="180" cy="18" r="2" fill="#f7f7f5" opacity="0.4" />
    <circle cx="296" cy="21" r="3" fill="#ff4500" />
  </svg>
);

const CHIP_PROMPTS = {
  "✈️ Explore Trips": "What trips does ROAMIES have right now?",
  "🌎 How ROAMIES Works": "How does ROAMIES work?",
  "🎒 Traveling Solo": "Can I join a trip alone? How does traveling solo with ROAMIES work?",
  "🧡 Join a Waitlist": "How do I join a waitlist?",
};

const REFERRAL_OPTIONS = [
  ["instagram", "Instagram"], ["tiktok", "TikTok"], ["facebook", "Facebook"], ["whatsapp", "WhatsApp"],
  ["friend_family", "Friend or Family"], ["google", "Google"], ["other", "Other"],
];

const ACTION_RE = /\[(HANDOFF|ACTION:[a-z_]+)\]/g;
const extractAction = (text) => {
  const m = text.match(ACTION_RE);
  return m ? m[0] : null;
};
const renderText = (text) => text.replace(ACTION_RE, "").trim();

const loadMsgs = () => {
  try { return JSON.parse(localStorage.getItem("roamie_msgs") || "[]"); } catch { return []; }
};

const splitChip = (chip) => {
  const [first, ...rest] = chip.split(" ");
  return { emoji: first, title: rest.join(" ") || first };
};

export const RoamieWidget = () => {
  const location = useLocation();
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat"); // chat | handoff
  const [messages, setMessages] = useState(loadMsgs);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/roamie/config").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false }));
  }, []);

  useEffect(() => {
    if (!cfg?.enabled || localStorage.getItem("roamie_intro_seen")) return;
    const show = setTimeout(() => setShowIntro(true), 3500);
    const hide = setTimeout(() => { setShowIntro(false); localStorage.setItem("roamie_intro_seen", "1"); }, 15500);
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, [cfg]);

  useEffect(() => {
    localStorage.setItem("roamie_msgs", JSON.stringify(messages.slice(-50)));
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open, view]);

  if (!cfg?.enabled) return null;

  const isSurvey = location.pathname.startsWith("/survey");
  const cid = () => localStorage.getItem("roamie_cid") || "";
  const started = messages.length > 0;

  const openChat = () => {
    setOpen(true);
    setShowIntro(false);
    localStorage.setItem("roamie_intro_seen", "1");
  };

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setView("chat");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    setMessages((m) => [...m, { role: "assistant", text: "", streaming: true }]);
    try {
      const res = await fetch(`${API}/roamie/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversation_id: cid(), message: text, source_page: location.pathname }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          let data;
          try { data = JSON.parse(p.slice(6)); } catch { continue; }
          if (data.token) {
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, text: last.text + data.token };
              return copy;
            });
          }
          if (data.done && data.conversation_id) {
            localStorage.setItem("roamie_cid", data.conversation_id);
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "I'm having trouble connecting right now — the ROAMIES team can help directly. Tap Contact the Team below and they'll follow up with you. " + HANDOFF_TOKEN };
        return copy;
      });
    } finally {
      setBusy(false);
      setMessages((m) => m.map((msg) => ({ ...msg, streaming: false })));
    }
  };

  const onChip = (chip) => {
    if (chip === "💬 Contact the Team") { setView("handoff"); return; }
    send(CHIP_PROMPTS[chip] || chip);
  };

  const renderAction = (token, key) => {
    if (!token) return null;
    const base = "mt-3 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] rounded-full px-4 py-2.5 border transition-all duration-200 group/act";
    if (token === HANDOFF_TOKEN) {
      return (
        <button key={key} onClick={() => setView("handoff")} data-testid="roamie-handoff-cta"
          className={`${base} text-flame border-flame/50 hover:bg-flame hover:text-white`}>
          Ask the team <ArrowRight className="w-3.5 h-3.5 group-hover/act:translate-x-0.5 transition-transform" />
        </button>
      );
    }
    if (token === "[ACTION:view_trips]") {
      return (
        <a key={key} href="/trips" data-testid="roamie-action-view-trips"
          className={`${base} text-cream/80 border-white/20 hover:border-flame hover:text-flame`}>
          View trips <ArrowRight className="w-3.5 h-3.5 group-hover/act:translate-x-0.5 transition-transform" />
        </a>
      );
    }
    if (token === "[ACTION:join_waitlist]") {
      return (
        <a key={key} href="/#waitlist" data-testid="roamie-action-join-waitlist"
          className={`${base} text-cream/80 border-white/20 hover:border-flame hover:text-flame`}>
          Join waitlist <ArrowRight className="w-3.5 h-3.5 group-hover/act:translate-x-0.5 transition-transform" />
        </a>
      );
    }
    if (token === "[ACTION:contact]") {
      return (
        <button key={key} onClick={() => openWhatsAppHub()} data-testid="roamie-action-contact"
          className={`${base} text-cream/80 border-white/20 hover:border-flame hover:text-flame`}>
          Contact ROAMIES <ArrowRight className="w-3.5 h-3.5 group-hover/act:translate-x-0.5 transition-transform" />
        </button>
      );
    }
    return null;
  };

  return (
    <>
      {/* Intro bubble — subtle first-visit hello */}
      <AnimatePresence>
        {showIntro && !open && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
            className={`fixed z-40 right-4 bottom-36 sm:right-6 sm:bottom-24 ${isSurvey ? "hidden sm:block" : ""}`}
            data-testid="roamie-intro-bubble">
            <div className="bg-[#101014] text-cream border border-white/15 rounded-2xl shadow-[0_16px_40px_-12px_rgba(255,69,0,0.4)] px-4 py-3 max-w-[250px] relative">
              <button onClick={() => { setShowIntro(false); localStorage.setItem("roamie_intro_seen", "1"); }}
                aria-label="Dismiss" data-testid="roamie-intro-dismiss"
                className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-flame text-white text-xs flex items-center justify-center">×</button>
              <button onClick={openChat} className="text-left text-sm font-semibold leading-snug">
                Hey 👋 I'm <span className="text-flame">Roamie</span> — ask me anything about trips, crews, or traveling solo.
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Launcher */}
      <AnimatePresence>
        {!open && (
          <motion.button initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }}
            onClick={openChat} data-testid="roamie-launcher-btn" aria-label="Ask Roamie"
            className={`fixed z-40 right-4 bottom-20 sm:right-6 sm:bottom-6 bg-[#0b0b0e]/95 backdrop-blur border border-white/15 rounded-full pl-1.5 pr-5 py-1.5 items-center gap-2.5 group hover:-translate-y-0.5 hover:border-flame/50 transition-all duration-300 shadow-[0_14px_36px_-10px_rgba(255,69,0,0.45),0_8px_24px_rgba(0,0,0,0.5)] ${isSurvey ? "hidden sm:flex" : "flex"}`}>
            <RoamieMark className="w-9 h-9 group-hover:rotate-45 transition-transform duration-500" />
            <span className="text-sm font-bold tracking-wide text-cream">Ask Roamie <span className="text-flame">✦</span></span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 44, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed z-50 inset-x-2 bottom-2 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[430px] h-[85dvh] sm:h-[620px] sm:max-h-[85vh] rounded-[26px] bg-[#0b0b0e] text-cream border border-white/10 shadow-[0_32px_80px_-16px_rgba(0,0,0,0.75),0_0_0_1px_rgba(255,69,0,0.07)] flex flex-col overflow-hidden"
            data-testid="roamie-chat-panel">

            {/* Header — layered dark concierge banner with route detail */}
            <div className="relative shrink-0 bg-gradient-to-b from-[#17171c] to-[#0d0d11] border-b border-white/[0.07] px-5 pt-4 pb-3.5 overflow-hidden">
              <RouteLine className="absolute inset-x-0 bottom-0 w-full h-8 opacity-60 pointer-events-none" />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3.5">
                  <div className="relative">
                    <RoamieMark className="w-11 h-11" />
                    <span className="absolute -inset-1 rounded-full border border-flame/25 animate-pulse pointer-events-none" />
                  </div>
                  <div>
                    <p className="font-display text-[22px] tracking-tight leading-none text-cream">ROAMIE</p>
                    <p className="text-[11px] text-cream/55 font-semibold mt-1">Your ROAMIES Travel Concierge</p>
                    <p className="text-[10px] text-flame font-bold uppercase tracking-[0.2em] mt-1 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-flame animate-pulse inline-block" /> Ready to roam ✦
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-0.5">
                  <button onClick={() => setOpen(false)} aria-label="Minimize Roamie" data-testid="roamie-minimize-btn"
                    className="p-2.5 rounded-full text-cream/50 hover:text-flame hover:bg-white/5 transition-colors"><Minus className="w-4 h-4" /></button>
                  <button onClick={() => setOpen(false)} aria-label="Close Roamie" data-testid="roamie-close-btn"
                    className="p-2.5 rounded-full text-cream/50 hover:text-flame hover:bg-white/5 transition-colors"><X className="w-4 h-4" /></button>
                </div>
              </div>
            </div>

            {view === "handoff" ? (
              <HandoffForm onBack={() => setView("chat")}
                onDone={() => { setView("chat"); setMessages((m) => [...m, { role: "assistant", text: CONFIRMATION_MSG }]); }} />
            ) : !started ? (
              /* Welcome state — the concierge lobby, not an empty messenger */
              <div className="flex-1 overflow-y-auto relative" data-testid="roamie-welcome-state">
                <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
                  style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #ff4500 0%, transparent 45%), radial-gradient(circle at 85% 75%, #ff4500 0%, transparent 40%)" }} />
                <div className="relative px-6 pt-9 pb-6 flex flex-col min-h-full">
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="mx-auto relative">
                    <RoamieMark className="w-16 h-16" />
                    <span className="absolute -inset-2 rounded-full border border-flame/20 pointer-events-none" />
                  </motion.div>
                  <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.45 }}
                    className="text-center text-xs font-bold tracking-[0.25em] uppercase text-flame mt-6">
                    Hey 👋 I'm Roamie
                  </motion.p>
                  <motion.h2 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18, duration: 0.45 }}
                    className="font-display text-4xl uppercase leading-[0.95] text-center mt-2 text-cream">
                    Where are we<br />roaming next<span className="text-flame">?</span>
                  </motion.h2>
                  <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.26, duration: 0.45 }}
                    className="text-center text-sm text-cream/55 leading-relaxed mt-3 max-w-[300px] mx-auto">
                    {cfg.welcome_message}
                  </motion.p>
                  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.34, duration: 0.45 }}
                    className="mt-7 space-y-2.5" data-testid="roamie-quick-actions">
                    {(cfg.quick_actions || []).map((chip, i) => {
                      const { emoji, title } = splitChip(chip);
                      return (
                        <button key={chip} onClick={() => onChip(chip)} data-testid={`roamie-chip-${i}`}
                          className="w-full flex items-center gap-3.5 p-3.5 rounded-2xl bg-white/[0.04] border border-white/10 hover:border-flame/60 hover:bg-flame/10 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] transition-all duration-200 group text-left">
                          <span className="w-9 h-9 shrink-0 rounded-xl bg-flame/15 border border-flame/25 flex items-center justify-center text-base">
                            {emoji}
                          </span>
                          <span className="text-sm font-bold text-cream/90 group-hover:text-cream flex-1">{title}</span>
                          <ArrowRight className="w-4 h-4 text-flame group-hover:translate-x-1 transition-transform" />
                        </button>
                      );
                    })}
                  </motion.div>
                  <p className="text-center text-[10px] uppercase tracking-[0.3em] text-cream/25 mt-auto pt-6 font-bold">
                    Small crews · Big trips · Ages 18–45
                  </p>
                </div>
              </div>
            ) : (
              /* Conversation */
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-5 bg-[#0b0b0e] relative" data-testid="roamie-messages">
                <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
                  style={{ backgroundImage: "radial-gradient(circle at 80% 10%, #ff4500 0%, transparent 40%)" }} />
                {messages.map((m, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
                    className={`relative flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    {m.role === "assistant" && <RoamieMark className="w-6 h-6 shrink-0 mt-1.5 mr-2.5" />}
                    <div className={m.role === "user"
                      ? "bg-gradient-to-br from-flame to-[#ff6a33] text-white px-4 py-3 rounded-2xl rounded-br-md shadow-[0_10px_28px_-10px_rgba(255,69,0,0.5)] text-sm font-medium max-w-[82%]"
                      : "bg-[#17171c] border border-flame/15 text-cream/90 px-4 py-3 rounded-2xl rounded-tl-md shadow-[0_10px_28px_-12px_rgba(0,0,0,0.8)] text-sm leading-relaxed max-w-[85%]"}
                      data-testid={m.role === "user" ? "roamie-user-message" : "roamie-assistant-message"}>
                      {m.streaming && !m.text ? (
                        <span className="flex items-center gap-1.5 py-1" data-testid="roamie-typing-indicator">
                          {[0, 1, 2].map((d) => (
                            <span key={d} className="w-2 h-2 bg-flame rounded-full animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
                          ))}
                        </span>
                      ) : (
                        <span className="whitespace-pre-wrap">{renderText(m.text)}</span>
                      )}
                      {m.role === "assistant" && !m.streaming && renderAction(extractAction(m.text), i)}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Composer */}
            {view === "chat" && (
              <form onSubmit={(e) => { e.preventDefault(); send(); }}
                className="shrink-0 p-3 bg-[#0d0d11] border-t border-white/[0.07]">
                <div className="flex items-center gap-2 rounded-2xl bg-[#16161b] border border-white/10 focus-within:border-flame/60 transition-colors pl-4 pr-1.5 py-1.5">
                  <input value={input} onChange={(e) => setInput(e.target.value)} maxLength={800}
                    placeholder="Ask Roamie anything…" data-testid="roamie-chat-input"
                    className="flex-1 bg-transparent border-none text-sm text-cream focus:outline-none placeholder:text-cream/30 h-9" />
                  <button type="submit" disabled={busy || !input.trim()} data-testid="roamie-send-btn" aria-label="Send message"
                    className="p-2.5 rounded-xl bg-flame hover:bg-[#ff5a1f] text-white transition-all disabled:opacity-30 disabled:hover:bg-flame active:scale-95">
                    <ArrowUp className="w-4 h-4" />
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const HandoffForm = ({ onBack, onDone }) => {
  const [form, setForm] = useState({ name: "", contact_method: "", email: "", phone: "", question: "", referral_source: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Please tell us your name.");
    if (!form.contact_method) return setError("Please choose how the team should contact you.");
    if (form.contact_method === "email" && !/[^@\s]+@[^@\s]+\.[^@\s]+/.test(form.email)) return setError("Please enter a valid email.");
    if (form.contact_method !== "email" && form.phone.replace(/\D/g, "").length < 7) return setError("Please enter a valid number.");
    setSaving(true);
    try {
      const r = await api.post("/roamie/handoff", {
        conversation_id: localStorage.getItem("roamie_cid") || "",
        name: form.name, contact_method: form.contact_method,
        email: form.contact_method === "email" ? form.email : "",
        phone: form.contact_method !== "email" ? form.phone : "",
        question: form.question, referral_source: form.referral_source,
      });
      if (r.data?.conversation_id) localStorage.setItem("roamie_cid", r.data.conversation_id);
      onDone();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const methods = [["whatsapp", "WhatsApp"], ["phone", "Phone"], ["email", "Email"]];
  const fieldCls = "w-full bg-white/[0.05] border border-white/15 focus:border-flame focus:outline-none px-3.5 py-3 text-sm text-cream placeholder:text-cream/30 rounded-xl transition-colors";
  const labelCls = "text-[11px] font-bold uppercase tracking-[0.15em] text-cream/45 block mb-1.5";

  return (
    <div className="flex-1 overflow-y-auto px-5 py-5 bg-[#0b0b0e]" data-testid="roamie-handoff-form">
      <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-flame mb-1.5">Human follow-up</p>
      <p className="font-display text-2xl uppercase leading-tight text-cream mb-1.5">Talk to the ROAMIES team</p>
      <p className="text-xs text-cream/45 mb-5">Only what's needed to follow up — nothing else.</p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>Your name *</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={120}
            data-testid="handoff-name-input" className={fieldCls} placeholder="Full name" />
        </div>
        <div>
          <label className={labelCls}>Preferred contact method *</label>
          <div className="grid grid-cols-3 gap-2">
            {methods.map(([key, label]) => (
              <button type="button" key={key} onClick={() => setForm({ ...form, contact_method: key })}
                data-testid={`handoff-method-${key}`}
                className={`py-3 text-xs font-bold uppercase tracking-wider rounded-xl border transition-all ${form.contact_method === key ? "bg-flame text-white border-flame" : "bg-white/[0.05] border-white/15 text-cream/70 hover:border-flame/50 hover:text-cream"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {form.contact_method === "email" && (
          <div>
            <label className={labelCls}>Email *</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="handoff-email-input" className={fieldCls} placeholder="you@email.com" />
          </div>
        )}
        {(form.contact_method === "whatsapp" || form.contact_method === "phone") && (
          <div>
            <label className={labelCls}>{form.contact_method === "whatsapp" ? "WhatsApp number *" : "Phone number *"}</label>
            <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="handoff-phone-input" className={fieldCls} placeholder="+1 555 123 4567" />
          </div>
        )}
        <div>
          <label className={labelCls}>Your question / details</label>
          <textarea value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} rows={3} maxLength={1000}
            data-testid="handoff-question-input" className={`${fieldCls} resize-none`}
            placeholder="What would you like the team to confirm?" />
        </div>
        <div>
          <label className={labelCls}>How did you hear about ROAMIES? (optional)</label>
          <select value={form.referral_source} onChange={(e) => setForm({ ...form, referral_source: e.target.value })}
            data-testid="handoff-referral-select" className={`${fieldCls} appearance-none [&>option]:bg-[#16161b]`}>
            <option value="">Prefer not to say</option>
            {REFERRAL_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        {error && <p className="text-xs font-bold text-flame" data-testid="handoff-error">{error}</p>}
        <button type="submit" disabled={saving} data-testid="handoff-submit-btn"
          className="w-full bg-flame hover:bg-[#ff5a1f] text-white font-bold uppercase tracking-[0.2em] text-xs py-4 rounded-xl transition-all disabled:opacity-50 active:scale-[0.99]">
          {saving ? "Sending..." : "Send to the team"}
        </button>
        <button type="button" onClick={onBack} data-testid="handoff-back-btn"
          className="w-full text-xs font-bold uppercase tracking-widest text-cream/40 hover:text-cream transition-colors py-1">
          ← Back to chat
        </button>
      </form>
    </div>
  );
};
